import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { CommandBlock } from "@/components/CommandBlock";
import { useConfirm } from "@/components/ConfirmDialog";
import { LoadError } from "@/components/LoadError";
import { SetupStep } from "@/components/SetupStep";
import { StatusPill } from "@/components/StatusPill";
import { TextInput } from "@/components/TextInput";
import { Toast, useToast } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { formatDateTime } from "@/lib/formatDateTime";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { SettingsSection, SettingsSkeleton } from "./SettingsSection";

type Provider = "AwsKms" | "AzureKeyVault";

interface EncryptionKeyStatus {
  provider: "Tendra" | Provider;
  keyId: string | null;
  awsRoleArn: string | null;
  azureTenantId: string | null;
  since: string | null;
  lastCheckedAt: string | null;
  availableSince: string | null;
  unavailableSince: string | null;
  unavailableReason: string | null;
  availableProviders: Provider[];
  externalId: string;
  awsAccountId: string | null;
  azureClientId: string | null;
}

const providerLabels: Record<Provider, string> = {
  AwsKms: "AWS KMS",
  AzureKeyVault: "Azure Key Vault",
};

const providerOptions: { value: Provider; description: string }[] = [
  {
    value: "AwsKms",
    description: "A symmetric KMS key in your AWS account.",
  },
  {
    value: "AzureKeyVault",
    description: "An RSA key in Azure Key Vault or Managed HSM.",
  },
];

const deletionWarning =
  "Tendra can't recover your organization's secrets if this key is deleted or access is permanently revoked. If you revoke access, Tendra will stop using the key within 5 minutes.";

function awsTrustPolicy(accountId: string, externalId: string) {
  return JSON.stringify(
    {
      Version: "2012-10-17",
      Statement: [
        {
          Effect: "Allow",
          Principal: { AWS: `arn:aws:iam::${accountId}:root` },
          Action: "sts:AssumeRole",
          Condition: { StringEquals: { "sts:ExternalId": externalId } },
        },
      ],
    },
    null,
    2,
  );
}

function awsPermissionsPolicy(keyArn: string) {
  return JSON.stringify(
    {
      Version: "2012-10-17",
      Statement: [
        {
          Effect: "Allow",
          Action: ["kms:Encrypt", "kms:Decrypt"],
          Resource: keyArn || "<key-arn>",
        },
      ],
    },
    null,
    2,
  );
}

function azureRoleCommand(keyId: string, clientId: string) {
  const match =
    /^https:\/\/([a-z0-9-]+)\.(vault|managedhsm)\.azure\.net\/keys\/([A-Za-z0-9-]+)/i.exec(
      keyId,
    );
  const vault = match?.[1] ?? "<vault-name>";
  const key = match?.[3] ?? "<key-name>";

  return match?.[2]?.toLowerCase() === "managedhsm"
    ? `az keyvault role assignment create --hsm-name ${vault} --role "Managed HSM Crypto Service Encryption User" --assignee ${clientId} --scope /keys/${key}`
    : `az role assignment create --role "Key Vault Crypto Service Encryption User" --assignee ${clientId} --scope "$(az keyvault show --name ${vault} --query id -o tsv)/keys/${key}"`;
}

type Choice = "Tendra" | Provider;

function ActionError({ message }: { message: string | null }) {
  return message ? (
    <p
      role="alert"
      className="mt-3 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {message}
    </p>
  ) : null;
}

export function Encryption() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { toast, showToast } = useToast();
  const { orgId } = useParams();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const { copiedField, copyToClipboard } = useCopyToClipboard();

  const [status, setStatus] = useState<EncryptionKeyStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [choice, setChoice] = useState<Choice | null>(null);
  const [replacingKey, setReplacingKey] = useState(false);
  const [keyId, setKeyId] = useState("");
  const [vaultUri, setVaultUri] = useState("");
  const [keyName, setKeyName] = useState("");
  const [roleArn, setRoleArn] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [testState, setTestState] = useState<"idle" | "ok">("idle");

  const fetchStatus = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    return api.get<EncryptionKeyStatus>(
      `/organizations/${orgId}/encryption-key`,
    );
  }, [accessToken, api, orgId]);

  const applyStatus = useCallback((data: EncryptionKeyStatus | null) => {
    if (data) {
      setStatus(data);
      setError(null);
    } else {
      setError("Couldn't load encryption settings.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchStatus().then((data) => {
      if (!cancelled) {
        applyStatus(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchStatus, applyStatus]);

  function resetForm() {
    setChoice(null);
    setReplacingKey(false);
    setKeyId("");
    setVaultUri("");
    setKeyName("");
    setRoleArn("");
    setTenantId("");
    setTestState("idle");
    setActionError(null);
  }

  function choose(next: Choice) {
    setChoice(next);
    setReplacingKey(false);
    setKeyId("");
    setVaultUri("");
    setKeyName("");
    setRoleArn("");
    setTenantId("");
    setTestState("idle");
    setActionError(null);
  }

  const selected: Choice | null = choice ?? status?.provider ?? null;
  const customerProvider =
    selected === "AwsKms" || selected === "AzureKeyVault" ? selected : null;

  const azureKeyId =
    vaultUri.trim() && keyName.trim()
      ? `${vaultUri.trim().replace(/\/+$/, "")}/keys/${keyName.trim()}`
      : "";
  const enteredKeyId =
    customerProvider === "AzureKeyVault" ? azureKeyId : keyId.trim();

  function requestBody() {
    return {
      provider: customerProvider,
      keyId: enteredKeyId,
      awsRoleArn: customerProvider === "AwsKms" ? roleArn.trim() : null,
      azureTenantId:
        customerProvider === "AzureKeyVault" ? tenantId.trim() : null,
    };
  }

  async function handleTest() {
    if (!orgId) {
      return;
    }

    setSubmitting(true);
    setActionError(null);
    setTestState("idle");

    try {
      await api.send(
        `/organizations/${orgId}/encryption-key/test`,
        "POST",
        requestBody(),
        "Tendra couldn't use this key.",
      );

      setTestState("ok");
      showToast("Tendra can encrypt and decrypt with this key.", "success");
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Tendra couldn't use this key.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUseKey() {
    if (!orgId || !customerProvider) {
      return;
    }

    const confirmed = await confirm(deletionWarning, {
      title: `Use this ${providerLabels[customerProvider]} key?`,
      confirmLabel: "Use this key",
      confirmVariant: "primary",
    });

    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setActionError(null);

    try {
      await api.send(
        `/organizations/${orgId}/encryption-key`,
        "PUT",
        requestBody(),
        "Couldn't switch to this key.",
      );

      resetForm();
      applyStatus(await fetchStatus());
      showToast("Your secrets are now protected by your key.", "success");
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Couldn't switch to this key.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUseTendraKey() {
    if (!orgId) {
      return;
    }

    const confirmed = await confirm(
      "Re-encrypts your secrets using a key managed by Tendra. Your current key must remain available until this process is complete.",
      {
        title: "Switch to Tendra's key?",
        confirmLabel: "Switch to Tendra's key",
        confirmVariant: "primary",
      },
    );

    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setActionError(null);

    try {
      await api.send(
        `/organizations/${orgId}/encryption-key`,
        "DELETE",
        undefined,
        "Couldn't switch to Tendra's key.",
      );

      resetForm();
      applyStatus(await fetchStatus());
      showToast("Your secrets are now protected by Tendra's key.", "success");
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : "Couldn't switch to Tendra's key.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const detailsEntered =
    enteredKeyId !== "" &&
    (customerProvider === "AwsKms"
      ? roleArn.trim() !== ""
      : tenantId.trim() !== "");

  const tested = testState === "ok";

  function testAndSwitchStep(label: string) {
    return (
      <SetupStep
        label={label}
        state={detailsEntered ? "current" : "todo"}
        title="Test and switch"
        last
      >
        <p className="mt-0.5 mb-3 text-sm text-foreground/70">
          Tendra verifies that it can encrypt and decrypt using your key before
          making any changes. Once you switch, all stored secrets are
          re-encrypted using your key.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary"
            onClick={handleTest}
            disabled={submitting || !detailsEntered}
          >
            {submitting ? "Testing..." : "Test key"}
          </Button>
          <Button
            variant="primary"
            onClick={handleUseKey}
            disabled={submitting || !detailsEntered}
          >
            Use this key
          </Button>
          {replacingKey && (
            <Button
              variant="secondary"
              onClick={resetForm}
              disabled={submitting}
            >
              Cancel
            </Button>
          )}
        </div>
        <ActionError message={actionError} />
      </SetupStep>
    );
  }

  const usesCustomerKey = status !== null && status.provider !== "Tendra";
  const stateSince = status?.unavailableSince ?? status?.availableSince ?? null;
  const choices: { value: Choice; label: string; description: string }[] =
    status
      ? [
          {
            value: "Tendra",
            label: "Tendra",
            description: "A key Tendra manages. No setup needed.",
          },
          ...providerOptions
            .filter(
              (option) =>
                status.availableProviders.includes(option.value) ||
                status.provider === option.value,
            )
            .map((option) => ({
              ...option,
              label: providerLabels[option.value],
            })),
        ]
      : [];
  const settingUpCustomerKey =
    customerProvider !== null &&
    (customerProvider !== status?.provider || replacingKey) &&
    (status?.availableProviders.includes(customerProvider) ?? false);
  const switchingToTendra = selected === "Tendra" && usesCustomerKey;

  return (
    <SettingsSection
      className="text-foreground"
      title="Encryption"
      description="Choose which key protects the secrets your organization stores in Tendra."
    >
      {!status && error ? (
        <LoadError
          what="encryption settings"
          onRetry={() => void fetchStatus().then(applyStatus)}
        />
      ) : !status ? (
        <SettingsSkeleton />
      ) : (
        <>
          {error && (
            <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          {usesCustomerKey && (
            <div className="mb-4 rounded-lg bg-surface p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold">
                  {`Your ${providerLabels[status.provider as Provider]} key`}
                </h3>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {status.unavailableSince ? (
                    <StatusPill variant="offline">Unavailable</StatusPill>
                  ) : (
                    <StatusPill variant="active">Available</StatusPill>
                  )}
                  {stateSince && (
                    <span className="text-foreground/70">
                      Since {formatDateTime(stateSince)}
                    </span>
                  )}
                </div>
              </div>
              {status.unavailableSince && (
                <p className="mt-3 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                  {`Tendra can't use this key because ${status.unavailableReason ?? "the key couldn't be used"}. Sessions that rely on stored credentials will fail, and new secrets can't be saved until access is restored.`}
                </p>
              )}
              <dl className="mt-2 space-y-1 text-sm">
                <div>
                  <dt className="inline text-foreground/70">Key: </dt>
                  <dd className="inline break-all">{status.keyId}</dd>
                </div>
                {status.awsRoleArn && (
                  <div>
                    <dt className="inline text-foreground/70">Role: </dt>
                    <dd className="inline break-all">{status.awsRoleArn}</dd>
                  </div>
                )}
                {status.azureTenantId && (
                  <div>
                    <dt className="inline text-foreground/70">Tenant: </dt>
                    <dd className="inline">{status.azureTenantId}</dd>
                  </div>
                )}
                <div>
                  <dt className="inline text-foreground/70">Last checked: </dt>
                  <dd className="inline">
                    {status.lastCheckedAt
                      ? formatRelativeTime(status.lastCheckedAt)
                      : "Not yet"}
                  </dd>
                </div>
              </dl>
              {status.availableProviders.includes(
                status.provider as Provider,
              ) &&
                !replacingKey && (
                  <Button
                    variant="secondary"
                    className="mt-3"
                    onClick={() => {
                      choose(status.provider as Provider);
                      setReplacingKey(true);
                    }}
                    disabled={submitting}
                  >
                    Change key
                  </Button>
                )}
            </div>
          )}

          <div className="rounded-lg bg-surface p-4">
            <ol className="list-none">
              <SetupStep
                label="1"
                state={
                  settingUpCustomerKey || switchingToTendra ? "done" : "current"
                }
                title="Choose where your key lives"
                last={!settingUpCustomerKey && !switchingToTendra}
              >
                <fieldset className="mt-3">
                  <legend className="sr-only">Key location</legend>
                  <div className="space-y-2">
                    {choices.map((option) => (
                      <label
                        key={option.value}
                        className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${
                          selected === option.value
                            ? "border-accent bg-surface/40"
                            : "border-muted bg-surface/40 hover:border-accent/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="keyLocation"
                          value={option.value}
                          checked={selected === option.value}
                          onChange={() => choose(option.value)}
                          disabled={submitting}
                          className="mt-1 h-4 w-4 shrink-0 accent-accent"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                              {option.label}
                            </span>
                            {status.provider === option.value && (
                              <Chip>Current</Chip>
                            )}
                          </span>
                          <span className="mt-0.5 block text-xs text-foreground/70">
                            {option.description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {status.availableProviders.length === 0 && (
                  <p className="mt-3 text-sm text-foreground/70">
                    Using a key from your own AWS or Azure account isn't
                    available yet.
                  </p>
                )}
              </SetupStep>

              {switchingToTendra && (
                <SetupStep
                  label="2"
                  state="current"
                  title="Switch to Tendra's key"
                  last
                >
                  <p className="mt-0.5 mb-3 text-sm text-foreground/70">
                    Re-encrypts your secrets with a key Tendra manages. Your own
                    key needs to be available for this to work.
                  </p>
                  <Button
                    variant="primary"
                    onClick={handleUseTendraKey}
                    disabled={submitting}
                  >
                    Switch to Tendra's key
                  </Button>
                  <ActionError message={actionError} />
                </SetupStep>
              )}

              {settingUpCustomerKey &&
                customerProvider === "AwsKms" &&
                status.awsAccountId && (
                  <>
                    <SetupStep
                      label="2"
                      state={tested ? "done" : "current"}
                      title="Give Tendra access to your key"
                    >
                      <div className="mt-3 space-y-4">
                        <div>
                          <label
                            htmlFor="keyId"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            KMS key ARN
                          </label>
                          <TextInput
                            id="keyId"
                            value={keyId}
                            onChange={(e) => {
                              setKeyId(e.target.value);
                              setTestState("idle");
                              setActionError(null);
                            }}
                            placeholder="arn:aws:kms:us-west-2:111122223333:key/1234abcd-..."
                            className="w-full"
                          />
                        </div>
                        <div>
                          <p className="mb-2 text-sm text-foreground/70">
                            Create an IAM role with the following trust policy
                            to allow Tendra to assume the role.
                          </p>
                          <CommandBlock
                            command={awsTrustPolicy(
                              status.awsAccountId,
                              status.externalId,
                            )}
                            field="trustPolicy"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        </div>
                        <div>
                          <p className="mb-2 text-sm text-foreground/70">
                            Attach this permissions policy to the role.
                          </p>
                          <CommandBlock
                            command={awsPermissionsPolicy(keyId.trim())}
                            field="permissionsPolicy"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="roleArn"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            IAM role ARN
                          </label>
                          <TextInput
                            id="roleArn"
                            value={roleArn}
                            onChange={(e) => {
                              setRoleArn(e.target.value);
                              setTestState("idle");
                              setActionError(null);
                            }}
                            placeholder="arn:aws:iam::111122223333:role/tendra-encryption"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </SetupStep>

                    {testAndSwitchStep("3")}
                  </>
                )}

              {settingUpCustomerKey &&
                customerProvider === "AzureKeyVault" &&
                status.azureClientId && (
                  <>
                    <SetupStep
                      label="2"
                      state={tested ? "done" : "current"}
                      title="Give Tendra access to your key"
                    >
                      <div className="mt-3 space-y-4">
                        <div>
                          <label
                            htmlFor="tenantId"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            Tenant ID
                          </label>
                          <TextInput
                            id="tenantId"
                            value={tenantId}
                            onChange={(e) => {
                              setTenantId(e.target.value);
                              setTestState("idle");
                              setActionError(null);
                            }}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="vaultUri"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            Vault URI
                          </label>
                          <TextInput
                            id="vaultUri"
                            value={vaultUri}
                            onChange={(e) => {
                              setVaultUri(e.target.value);
                              setTestState("idle");
                              setActionError(null);
                            }}
                            placeholder="https://your-vault.vault.azure.net/"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="keyName"
                            className="mb-1.5 block text-sm text-foreground"
                          >
                            Key name
                          </label>
                          <TextInput
                            id="keyName"
                            value={keyName}
                            onChange={(e) => {
                              setKeyName(e.target.value);
                              setTestState("idle");
                              setActionError(null);
                            }}
                            placeholder="tendra"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <p className="mb-2 text-sm text-foreground/70">
                            Create an enterprise application in your tenant for
                            Tendra.
                          </p>
                          <CommandBlock
                            command={`az ad sp create --id ${status.azureClientId}`}
                            field="servicePrincipal"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        </div>
                        <div>
                          <p className="mb-2 text-sm text-foreground/70">
                            Allow Tendra to wrap and unwrap secrets with your
                            key.
                          </p>
                          <CommandBlock
                            command={azureRoleCommand(
                              azureKeyId,
                              status.azureClientId,
                            )}
                            field="roleAssignment"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        </div>
                      </div>
                    </SetupStep>

                    {testAndSwitchStep("3")}
                  </>
                )}
            </ol>
          </div>
        </>
      )}

      {confirmDialog}
      <Toast toast={toast} />
    </SettingsSection>
  );
}
